import { Autocomplete, Button, Grid, Stack, TextField, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { HealthcareServiceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import { OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import {
  ValueSetWrapper,
  WrappedValueSet,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import {
  useHealthcareServices,
  useOrganization,
  useValueSets,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import CustomModal, {
  CustomModalBasicProps,
  GridItem,
  GridSection,
} from 'src/components/CustomModal';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useObjectState from 'src/hooks/useObjectState';
import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import { IconWrapperStyle } from 'src/sections/@dashboard/general/e-commerce/EcommerceWidgetSummary';
import { ActionData } from 'src/careflow/care-coordination/case/goal/grid/GoalModal';
import SearchMember, {
  MemberResourceTypeOption,
  SearchMemberProps,
} from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMember';
import {
  TaskSelectedMemberOption,
  TaskSelectedMemberValue,
  getOwnerDisplayLabel,
} from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';
import { getValueSetConceptValue } from 'src/careflow/helpers/common';
import { getFhirIdFromEntity, getWrappedByReference } from 'src/utils/fhir';
import { isValidJsonString } from 'src/utils/string';
import ReferralAutocomplete, {
  ReferralAutocompleteOption,
  ReferralOptionCode,
} from '../ReferralAutocomplete';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { getResourceByReference } from 'src/careflow/helpers/incident';

export type ReferralReferedOption = MemberResourceTypeOption;

interface ReferralInformationFormState {
  referred: TaskSelectedMemberValue | null;
  cause: ValueSetComposeIncludeConcept | null;
  referredDetails: string | null;
  causeDetails: string | null;
  priority: ValueSetComposeIncludeConcept | null;
}

interface ReferralSelectedMemberState {
  isSearchMemberOpen: boolean;
  selectedReferred: TaskSelectedMemberOption | TaskSelectedMemberValue | null;
  referredOptions: (TaskSelectedMemberOption | TaskSelectedMemberValue)[];
}
export interface ErrorFieldState {
  referralCause?: string | null;
  referralReferred?: string | null;
  referralPriority?: string | null;
}

interface Props extends CustomModalBasicProps {
  patient: WrappedPatient | null;
  serviceRequest: WrappedServiceRequest | null;
  open: boolean;
  isLoading: boolean;
  handlerSave: (data: any) => Promise<any>;
  setRelatedPersonOpen: Dispatch<SetStateAction<boolean>>;
  checkListData?: any;
}

export default function ReferralInformation({
  patient,
  serviceRequest,
  open,
  isLoading,
  onClose,
  handlerSave,
  setRelatedPersonOpen,
  checkListData,
}: Props) {
  const { i18n } = useLocales();
  const currentUser = useAuth().getCurrentUser();
  const { componentsData } = useTenantConfigData();
  const { externalOrganizationCode } = componentsData?.workflow?.referralInformation ?? {};

  const metadata = useMemo(() => {
    try {
      const metadata = checkListData?.metadata ? JSON.parse(checkListData.metadata) : {};
      const {
        serviceType = 'enroll-to-sbha-program',
        serviceCategory = 'intake-referral',
        servicePathCategory = 'inbound-referral',
        showPriority = false,
        showReferredDetails = false,
      } = metadata?.ui?.data ?? {};

      return {
        serviceType,
        serviceCategory,
        servicePathCategory,
        showPriority,
        showReferredDetails,
      };
    } catch (err) {
      return {
        serviceType: null,
        serviceCategory: null,
        servicePathCategory: null,
        showPriority: false,
        showReferredDetails: false,
      };
    }
  }, [checkListData]);

  const title = [
    `${i18n('patientEngagement.details.title', 'engagement')}`,
    `${i18n('patientEngagement.details.workflow.step.assignCoordinator', 'engagement')}`,
    `${i18n(
      'patientEngagement.details.workflow.checklist.referralInformation.title',
      'engagement'
    )}`,
  ];

  const [organization, { isFetching: isOrganizationFetching }] = useOrganization(
    getFhirIdFromEntity(currentUser?.organization_fhir_uri),
    {
      autofetch: currentUser?.organization_fhir_uri ? true : false,
      map: OrganizationWrapper,
    }
  );

  const [healthCareService, { isFetching: isLoadingHealthcareServide }] = useHealthcareServices({
    filter: { 'service-type': metadata?.serviceType },
    map: HealthcareServiceWrapper,
    autofetch: !!metadata?.serviceType,
  });

  const valueSetsIds = [
    'ph-service-request-category',
    'ph-service-request-type',
    'ph-reason-referral',
    'ph-service-request-path-category',
    metadata?.showPriority && 'ph-service-priority',
  ];

  const [valueSets, { isFetching: isValueSetsFetching }] = useValueSets({
    filter: {
      _id: valueSetsIds.join(),
    },
    map: ValueSetWrapper,
    autofetch: !!metadata?.serviceType,
  });

  const [
    serviceRequestCategory,
    serviceRequestType,
    reasonReferral,
    serviceRequestPathCategory,
    serviceRequestPriority,
  ] = useMemo(() => {
    if (!valueSets?.length) return [];

    return valueSetsIds.map((valueSetId) => valueSets.find(({ id }) => id === valueSetId) ?? null);
  }, [valueSets]);

  const setServiceRequestType = (serviceRequestType?.asListAll?.() ?? []).find(
    ({ code }) => code === metadata?.serviceType
  );

  const setServiceRequestCategory = (serviceRequestCategory?.asListAll?.() ?? []).find(
    ({ code }) => code === metadata?.serviceCategory
  );

  const setServiceRequestPathCatetory = (serviceRequestPathCategory?.asListAll?.() ?? []).find(
    ({ code }) => code === metadata?.servicePathCategory
  );

  const [{ referred, cause, referredDetails, causeDetails, priority }, updateState] =
    useObjectState<ReferralInformationFormState>(
      getInitialState({
        serviceRequest: null,
        reasonReferralValues: null,
        serviceRequestPriorityValues: null,
      })
    );

  const [{ isSearchMemberOpen, selectedReferred, referredOptions }, updateSelectedMember] =
    useObjectState<ReferralSelectedMemberState>(getInitialSelectedState(!!patient));

  const [errors, updateErrorState] = useObjectState<ErrorFieldState>({
    referralReferred: null,
    referralCause: null,
    referralPriority: null,
  });

  const isAllLoading =
    isLoading || isValueSetsFetching || isOrganizationFetching || isLoadingHealthcareServide;

  useEffect(() => {
    if (!open) return;

    updateState(
      getInitialState({
        serviceRequest,
        reasonReferralValues: reasonReferral,
        serviceRequestPriorityValues: serviceRequestPriority,
      })
    );

    const getReferredFromServiceRequest = async () => {
      const { referred } = await getReferred(serviceRequest);
      updateState({ referred: referred });
      updateSelectedMember({ selectedReferred: referred });
    };

    getReferredFromServiceRequest();

    updateErrorState({
      referralReferred: null,
      referralCause: null,
      referralPriority: null,
    });
    updateSelectedMember(getInitialSelectedState(!!patient));
  }, [open, serviceRequest, patient, reasonReferral, serviceRequestPriority, isAllLoading]);

  const handleOnSave = async () => {
    const errors: ErrorFieldState = {};

    if (isEmpty(referred)) {
      errors.referralReferred = 'Must specify a Referred';
    }

    if (isEmpty(cause)) {
      errors.referralCause = 'Must specify a Cause';
    }

    if (metadata?.showPriority && !priority) {
      errors.referralPriority = 'Must specify the priority';
    }

    if (Object.keys(errors).length) {
      updateErrorState({ ...errors });
      return;
    }

    updateErrorState({
      referralReferred: null,
      referralCause: null,
      referralPriority: null,
    });

    const selectedPriorityCode = priority?.code ?? null;

    const { payload: serviceRequestData } = mapServiceRequest(selectedPriorityCode);

    await handlerSave(serviceRequestData);
    onClose?.({}, 'backdropClick');
  };

  const mapServiceRequest = (priorityCode: string | null = null): ActionData => {
    const payload = {
      id: serviceRequest?.id ?? null,
      ...(referred
        ? {
            referredFrom: {
              reference: `${referred?.resourceType}/${referred?.id}`,
              display: `${referredDetails ? `${referredDetails}` : ''}`,
            },
          }
        : null),
      ...(cause
        ? {
            reasonCode: [
              {
                coding: [cause],
                text: `${causeDetails ? `${causeDetails}` : ''}`,
              },
            ],
          }
        : null),
      ...(priorityCode ? { priority: priorityCode } : null),
      intent: 'directive',
      status: 'active',
      ...(!serviceRequest?.id && {
        category: [
          {
            coding: [setServiceRequestCategory],
            text: setServiceRequestCategory?.display,
          },
          {
            coding: [setServiceRequestPathCatetory],
            text: setServiceRequestPathCatetory?.display,
          },
        ],
        serviceType: {
          coding: [setServiceRequestType],
          text: setServiceRequestType?.display,
        },
        patientId: patient?.id,
        patientName: patient?.getFullName(),
        serviceId: healthCareService?.[0]?.id
          ? `HealthcareService/${healthCareService?.[0]?.id}`
          : null,
        serviceName: healthCareService?.[0]?.name ?? null,
      }),
    };

    return {
      payload,
      actionCall: () => {},
      successfulMessage: '',
    };
  };

  const [selectedReferredCodeOption, setSelectedReferredCodeOption] = useState<string>();

  function getReferredOptionLabel() {
    const resource = referred;

    const resolvers: Record<ReferralOptionCode | 'default', () => string | undefined> = {
      default: () => (resource ? getDisplay(resource) : undefined),

      internal: () => (organization ? getDisplay(organization) : undefined),

      external: () =>
        resource?.resourceType === 'Organization' ? getDisplay(resource) : undefined,

      'parent-guardian': () => {
        if (resource?.resourceType !== 'RelatedPerson') return;

        const display = getDisplay(resource);
        const relationship = resource?.relationship?.[0]?.coding?.[0]?.display;

        return relationship ? `${display} (${relationship})` : display;
      },

      self: () => (patient ? getDisplay(patient) : undefined),
    };

    const key: ReferralOptionCode = (selectedReferredCodeOption as ReferralOptionCode) ?? 'default';

    return resolvers[key]();
  }

  const referredOptionLabel = useMemo(getReferredOptionLabel, [
    selectedReferredCodeOption,
    patient,
    organization,
    referred,
  ]);

  function handleReferralAutocompleteOnChangeOption(
    option: ReferralAutocompleteOption | undefined
  ) {
    if (!option) {
      setSelectedReferredCodeOption(undefined);
      updateSelectedMember({ selectedReferred: null });
      updateState({ referred: null });
      return;
    }

    updateErrorState({ referralReferred: null });

    setSelectedReferredCodeOption(option.code);

    if (option.code === 'internal') {
      updateSelectedMember({
        isSearchMemberOpen: false,
        selectedReferred: organization,
      });
      updateState({ referred: organization });
      return;
    } else if (option.code === 'external') {
      const rOption: TaskSelectedMemberOption = {
        label: 'External Referral',
        value: 'Organization',
      };

      updateSelectedMember({
        isSearchMemberOpen: true,
        selectedReferred: rOption,
      });
      return;
    } else if (option.code === 'self') {
      updateSelectedMember({
        isSearchMemberOpen: false,
        selectedReferred: patient,
      });
      updateState({ referred: patient });
      return;
    } else if (option.code === 'parent-guardian') {
      const rOption: TaskSelectedMemberOption = {
        label: 'Parent / Guardian',
        value: 'RelatedPerson',
      };

      updateSelectedMember({
        isSearchMemberOpen: true,
        selectedReferred: rOption,
      });
      return;
    }

    updateSelectedMember({ selectedReferred: null });
    updateState({ referred: null });
  }

  const handleAddNewParentGuardian = () => {
    setRelatedPersonOpen(true);
  };

  const ParentGuardianHeader = () =>
    // @ts-ignore
    selectedReferred?.value === 'RelatedPerson' ? (
      <Button sx={{ marginLeft: 'auto' }} onClick={handleAddNewParentGuardian}>
        {`${i18n(
          'patientEngagement.details.workflow.checklist.referralInformation.addNew',
          'engagement'
        )}`}
      </Button>
    ) : null;

  return (
    <CustomModal
      keepMounted
      open={open}
      title={title[2]}
      breadcrumbs={title}
      onSave={handleOnSave}
      onCancel={onClose as Function}
      onClose={onClose}
      isLoading={isAllLoading}
      containerSx={[
        { overflow: 'scroll' },
        isSearchMemberOpen
          ? { width: '94vw' }
          : { width: metadata?.showReferredDetails ? '70vw' : '40vw' },
      ]}
    >
      <Grid container my={3} justifyContent={'space-between'}>
        <Grid item xs={isSearchMemberOpen ? 6 : 12}>
          <GridSection mt={0}>
            <Typography variant="caption" sx={{ color: 'gray' }}>
              {`${i18n(
                'patientEngagement.details.workflow.checklist.referralInformation.comment',
                'engagement'
              )}`}
            </Typography>
          </GridSection>
          <GridSection
            id="referral-information-section-1"
            flexDirection={metadata?.showReferredDetails ? 'row' : 'column'}
          >
            <GridItem
              id="referral-information-question1"
              xs={metadata?.showReferredDetails ? 5.8 : 12}
            >
              <Stack direction="row" alignItems="center" mb={2}>
                <IconWrapperStyle sx={{ bgcolor: 'green', color: 'white' }}>1 </IconWrapperStyle>
                <Typography variant="body2" component="span" noWrap sx={{ color: 'text.primary' }}>
                  &nbsp;
                  {`${i18n(
                    'patientEngagement.details.workflow.checklist.referralInformation.question1',
                    'engagement'
                  )}`}
                </Typography>
              </Stack>
              <Stack spacing={2} pl={1}>
                <ReferralAutocomplete
                  patient={patient}
                  errors={errors}
                  optionLabel={referredOptionLabel}
                  onChangeOption={handleReferralAutocompleteOnChangeOption}
                />
                {metadata?.showReferredDetails && (
                  <TextField
                    fullWidth
                    rows={4}
                    multiline
                    disabled={isAllLoading}
                    value={referredDetails ?? ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      updateState({
                        referredDetails: event.target.value,
                      });
                    }}
                    label={i18n(
                      'patientEngagement.details.workflow.checklist.referralInformation.details',
                      'engagement'
                    )}
                    placeholder={`Write Some ${i18n(
                      'patientEngagement.details.workflow.checklist.referralInformation.details',
                      'engagement'
                    )}...`}
                    variant="outlined"
                  />
                )}
              </Stack>
            </GridItem>
            <GridItem
              id="referral-information-question2"
              xs={metadata?.showReferredDetails ? 5.8 : 12}
              sx={metadata?.showReferredDetails ? {} : { mt: 2 }}
            >
              <Stack direction="row" alignItems="center" mb={2}>
                <IconWrapperStyle sx={{ bgcolor: 'green', color: 'white' }}>2</IconWrapperStyle>
                <Typography variant="body2" component="span" noWrap sx={{ color: 'text.primary' }}>
                  &nbsp;
                  {`${i18n(
                    'patientEngagement.details.workflow.checklist.referralInformation.question2',
                    'engagement'
                  )}`}
                </Typography>
              </Stack>
              <Stack spacing={2} pl={1}>
                <Autocomplete
                  disabled={isAllLoading}
                  value={cause}
                  fullWidth
                  onChange={(_: React.SyntheticEvent, cause) => {
                    updateErrorState({ referralCause: null });
                    updateState({ cause });
                  }}
                  options={reasonReferral?.asListAll?.() ?? []}
                  getOptionLabel={({ display }) => display as string}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors?.referralCause}
                      helperText={errors?.referralCause}
                      label={`${i18n(
                        'patientEngagement.details.workflow.checklist.referralInformation.cause',
                        'engagement'
                      )}*`}
                      placeholder={`Select ${i18n(
                        'patientEngagement.details.workflow.checklist.referralInformation.cause',
                        'engagement'
                      )}...`}
                      variant="outlined"
                    />
                  )}
                />
                <TextField
                  fullWidth
                  rows={4}
                  multiline
                  disabled={isAllLoading}
                  value={causeDetails ?? ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    updateState({
                      causeDetails: event.target.value,
                    });
                  }}
                  label={i18n(
                    'patientEngagement.details.workflow.checklist.referralInformation.details',
                    'engagement'
                  )}
                  placeholder={`Write Some ${i18n(
                    'patientEngagement.details.workflow.checklist.referralInformation.details',
                    'engagement'
                  )}...`}
                  variant="outlined"
                />
              </Stack>
            </GridItem>
          </GridSection>
          {metadata?.showPriority && (
            <GridSection id="referral-information-section-3">
              <GridItem
                id="referral-information-question3"
                xs={metadata?.showReferredDetails ? 5.8 : 12}
              >
                <Stack direction="row" alignItems="center" mb={2} mt={-1}>
                  <IconWrapperStyle sx={{ bgcolor: 'green', color: 'white' }}>3</IconWrapperStyle>
                  <Typography
                    variant="body2"
                    component="span"
                    noWrap
                    sx={{ color: 'text.primary' }}
                  >
                    &nbsp;
                    {`${i18n(
                      'patientEngagement.details.workflow.checklist.referralInformation.question3',
                      'engagement'
                    )}`}
                  </Typography>
                </Stack>
                <Stack spacing={2} pl={1}>
                  <Autocomplete
                    disabled={isAllLoading}
                    value={priority}
                    fullWidth
                    onChange={(_: React.SyntheticEvent, priority) => {
                      updateErrorState({ referralPriority: null });
                      updateState({ priority });
                    }}
                    options={serviceRequestPriority?.asListAll?.() ?? []}
                    getOptionLabel={({ display }) => display as string}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!errors?.referralPriority}
                        helperText={errors?.referralPriority}
                        label={`${i18n(
                          'patientEngagement.details.workflow.checklist.referralInformation.priority',
                          'engagement'
                        )}*`}
                        placeholder={`Select ${i18n(
                          'patientEngagement.details.workflow.checklist.referralInformation.priority',
                          'engagement'
                        )}...`}
                        variant="outlined"
                      />
                    )}
                  />
                </Stack>
              </GridItem>
            </GridSection>
          )}
        </Grid>
        {isSearchMemberOpen && (
          <Grid item xs={5.9}>
            <SearchMember
              patient={patient as WrappedPatient}
              onSelectResource={(resource) => {
                updateSelectedMember({ selectedReferred: resource, isSearchMemberOpen: false });
                updateState({ referred: resource });
              }}
              filterOrganizationCode={externalOrganizationCode}
              onClear={() => {
                updateSelectedMember({ selectedReferred: null, isSearchMemberOpen: false });
                updateState({ referred: null });
              }}
              externalResourceType={(() => {
                const selectedOption = selectedReferred as ReferralReferedOption;
                return selectedOption?.value as SearchMemberProps['externalResourceType'];
              })()}
              headerComponents={<ParentGuardianHeader />}
            />
          </Grid>
        )}
      </Grid>
    </CustomModal>
  );
}

const getMemberResourceTypes = (showPatient: boolean = true): MemberResourceTypeOption[] => [
  { label: 'Internal Referral', value: 'Organization' },
  { label: 'External Referral', value: 'Organization' },
  { label: 'Parent / Guardian', value: 'RelatedPerson' },
  ...(showPatient
    ? [{ label: 'Self-Referral', value: 'Patient' } as MemberResourceTypeOption]
    : []),
];

export const getReferred = async (serviceRequest: WrappedServiceRequest | null) => {
  const getReferred: ReferralInformationFormState['referred'] = await getWrappedByReference(
    serviceRequest?.requester
  );
  const referred = getReferred;
  const referredDetails = serviceRequest?.requester?.display ?? null;

  return { referred, referredDetails };
};

const getInitialState = ({
  serviceRequest,
  reasonReferralValues,
  serviceRequestPriorityValues,
}: {
  serviceRequest: WrappedServiceRequest | null;
  reasonReferralValues: WrappedValueSet | null;
  serviceRequestPriorityValues: WrappedValueSet | null;
}): ReferralInformationFormState => {
  const referredDetails = serviceRequest?.requester?.display ?? null;
  const getReasonCode = serviceRequest?.reasonCode?.[0] ?? null;
  const cause =
    getValueSetConceptValue(
      reasonReferralValues?.asListAll() ?? [],
      getReasonCode?.coding?.[0]?.code
    ) ?? null;
  const causeDetails = getReasonCode?.text ?? null;
  const priority =
    getValueSetConceptValue(
      serviceRequestPriorityValues?.asListAll() ?? [],
      serviceRequest?.priority ?? undefined
    ) ?? null;

  return {
    referred: null,
    cause,
    referredDetails,
    causeDetails,
    priority,
  };
};

const getInitialSelectedState = (showPatient: boolean) => ({
  isSearchMemberOpen: false,
  selectedReferred: null,
  referredOptions: [...getMemberResourceTypes(showPatient)],
});

export const getDisplay = (option: TaskSelectedMemberValue): string => {
  const ownerDisplayLabel = getOwnerDisplayLabel(option);
  const validDisplay = isValidJsonString(ownerDisplayLabel);
  const display = validDisplay ? JSON.parse(ownerDisplayLabel)?.display : ownerDisplayLabel;
  return display;
};

export const getFhirTerminologyNormative = async (option?: string, reference?: string) => {
  if (!option || !reference) return null;

  try {
    const resource = await getResourceByReference(reference);
    if (!resource?.id) return null;
    const data = resource?.resourceType === 'ValueSet' ? ValueSetWrapper(resource) : null;
    const list = data?.asListAll?.() ?? [];

    // Exact code match first
    let match = list.find((e: any) => e?.code === option);
    if (match) return match.display ?? match.code ?? option;

    // Exact display match
    match = list.find((e: any) => e?.display === option);
    if (match) return match.display ?? match.code ?? option;

    // Case-insensitive match on code or display
    const lower = option.toLowerCase();
    match = list.find(
      (e: any) =>
        (e?.code || '').toLowerCase() === lower || (e?.display || '').toLowerCase() === lower
    );
    if (match) return match.display ?? match.code ?? option;

    return null;
  } catch (err) {
    // avoid breaking callers on unexpected failures
    // eslint-disable-next-line no-console
    console.error('getFhirTerminologyNormative error', err);
    return null;
  }
};
